// app/javascript/components/FallbackGroupChannelPage.jsx

import React, {useEffect, useRef, useState} from "react";
import FallbackMessageBody from "./FallbackMessageBody";
import CustomMessageInput from "./CustomMessageInput";

export default function FallbackGroupChannelPage({conversationId, channelUrl, userId, initialSinceMs = null}) {
    const [messages, setMessages] = useState([]);
    const listRef = useRef(null);
    const sinceRef = useRef(initialSinceMs || Date.now());
    const abortRef = useRef(null);

    useEffect(() => {
        $.ajax({
            url: `/conversations/${conversationId}/get_messages`,
            method: 'GET',
            dataType: 'json'
        }).then((result) => {
                setMessages(result);
            },
            (error) => {
                window.sentry?.captureException(error);
            })
    }, [conversationId]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        let isMounted = true;

        const poll = async () => {
            // Don't open a connection while the tab is backgrounded
            if (!isMounted || document.visibilityState === 'hidden') return;

            try {
                const url = `/polls/group_channels/${encodeURIComponent(channelUrl)}?since_ms=${sinceRef.current}`;
                const xhr = $.ajax({url, method: 'GET', dataType: 'json'});
                abortRef.current = xhr;
                const data = await xhr;

                if (!isMounted) return;
                abortRef.current = null;

                if (Array.isArray(data.messages) && data.messages.length) {
                    setMessages((prev) => {
                        const seen = new Set(prev.map((m) => m.id ?? m.created_at));
                        const incoming = data.messages.filter((m) => {
                            const key = m.id ?? m.created_at;
                            if (seen.has(key)) return false;
                            seen.add(key);
                            return true;
                        });
                        if (incoming.length) {
                            sinceRef.current = Math.max(sinceRef.current, data.last_message_timestamp);
                            return [...prev, ...incoming];
                        }
                        return prev;
                    });
                }
            } catch (e) {
                if (!isMounted || e.statusText === 'abort') return;
                window.sentry?.captureException(e);
            }
        };

        poll(); // immediate first poll on mount / channel change
        const intervalId = setInterval(poll, 10000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
            abortRef.current?.abort();
            abortRef.current = null;
        };
    }, [channelUrl]);


    function handleMessageSent(newMessage) {
        if (!newMessage) return;
        setMessages((prev) => {
            const key = newMessage.id ?? newMessage.created_at;
            const exists = prev.some(m => (m.id ?? m.created_at) === key);
            return exists ? prev : [...prev, newMessage];
        });
    }

    return (
        <div className="sendbird-limited-service-container">
            <div className="sendbird-limited-service-banner">Limited service - messaging improvements rolling out this
                week
            </div>
            <div className="sendbird-limited-service-feed-list" ref={listRef}>
                {messages.map((message) => {
                    return (
                        <FallbackMessageBody
                            message={message}
                            channelUrl={channelUrl}
                            userId={userId}
                            conversationId={conversationId}
                        />
                    )
                })}
            </div>
            <CustomMessageInput
                conversationId={conversationId}
                onMessageSent={handleMessageSent}
                fallbackMode={true}
            />
        </div>
    );
}
